import React from 'react'
import { Alert, Anchor, List, Stack, Text, Title } from '@mantine/core'

import { CourtReporterPageEvent } from './types'

import { EventFormat } from '@/apiClient'
import { Card } from '@/components/Event/Detail/components/Card'
import { JoinCallButton } from '@/components/Event/Detail/components/LinksCard'
import {
  COURT_REPORTER_EXHIBITS_EMAIL,
  COURT_REPORTER_SUPPORT_PHONE,
} from '@/constants'
import { getEventFormat } from '@/utils/parrot'

const SupportPhoneLink: React.FC = () => (
  <Anchor href={`tel:${COURT_REPORTER_SUPPORT_PHONE}`} inherit>
    {COURT_REPORTER_SUPPORT_PHONE}
  </Anchor>
)

export const RemindersAlert: React.FC = () => (
  <Alert title="Your Role & On-Record Reminders">
    <List size="sm" spacing="xs">
      <List.Item>
        A Session Assistant will be present to represent Filevine and handle any
        technical or administrative tasks.{' '}
        <b>The court reporter is always the Guardian of the Record.</b>
      </List.Item>
      <List.Item>
        Remember to ask for the{' '}
        <b>orders for all parties (including the taking attorney)</b> on the
        record.
      </List.Item>
      <List.Item>
        If a read and sign is not addressed by counsel, inquire on the record
        whether the witness wishes to review and sign the transcript, consistent
        with applicable rules and client instructions.
      </List.Item>
    </List>
  </Alert>
)

export const GetReadyCard: React.FC<{ event: CourtReporterPageEvent }> = ({
  event,
}) => (
  <Card heading="Get ready for the deposition">
    {getEventFormat(event) === EventFormat.on_site ? (
      <List type="ordered" withPadding size="sm">
        <List.Item>
          Plan to travel to the venue with plenty of time to account for
          traffic.
        </List.Item>
        <List.Item>
          Please arrive to the venue at least 30 minutes early.
        </List.Item>
        <List.Item>
          If you run into any delays, call our team at <SupportPhoneLink />.
        </List.Item>
      </List>
    ) : (
      <List type="ordered" withPadding size="sm">
        <List.Item>
          Join 15-30 minutes prior to make sure you're all set up.
        </List.Item>
        <List.Item>
          Make sure to join using a computer/laptop. Tablets and phones have
          limited features.
        </List.Item>
        <List.Item>Be in a quiet and well-lit environment.</List.Item>
        <List.Item>
          Make sure you have a working microphone and speakers.
        </List.Item>
        <List.Item>Check if your camera is working.</List.Item>
      </List>
    )}
  </Card>
)

export const ContactInfoAlert: React.FC = () => (
  <Alert title="Contact Info" color="orange">
    <Stack spacing="xs">
      <Text size="sm">
        <b>Do not share your personal email</b> with parties involved in the
        proceeding to receive exhibits. Instead, provide:{' '}
        <b>{COURT_REPORTER_EXHIBITS_EMAIL}</b>
      </Text>
      <Text size="sm">
        For any emergencies or questions:{' '}
        <b>
          <SupportPhoneLink />
        </b>
      </Text>
    </Stack>
  </Alert>
)

export const CancelBookingContactCard: React.FC = () => (
  <Card heading="Do you need to cancel this booking?">
    <Text size="sm">
      Please reach out to our team at <SupportPhoneLink />.
    </Text>
  </Card>
)

export const EventLinksCard: React.FC<{ event: CourtReporterPageEvent }> = ({
  event,
}) => (
  <Card heading="Event links">
    <Stack align="flex-start">
      <Text size="sm">Before the event will start, open this link.</Text>
      <JoinCallButton event={event} asHost={false} />
    </Stack>
  </Card>
)

export const InProgressAlert: React.FC<{ event: CourtReporterPageEvent }> = ({
  event,
}) => (
  <Alert title="Event is in progress">
    <Stack align="flex-start">
      <Text size="sm">
        This page is for reference during the event — if you are in-person you
        do not need to have this available.
      </Text>
      <JoinCallButton event={event} asHost={false} />
      <Text size="xs" color="dimmed">
        You are not required to join the Zoom if you are transcribing the
        deposition in-person.
      </Text>
    </Stack>
  </Alert>
)

export const SupportAlert: React.FC = () => (
  <Alert title="Need help during the event?" color="red">
    <Stack spacing="xs">
      <Text size="sm">Call our support line and we'll jump in right away.</Text>
      <Title order={3}>
        <SupportPhoneLink />
      </Title>
    </Stack>
  </Alert>
)
